import { Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { MONITORING_BOM_SERVICE_DEFINITIONS, MonitoringBomServiceId } from "@domain/project/monitoring-bom-service-definition";
import { MonitoringProductData } from "@domain/project/monitoring-product-data/monitoring-product-data";
import { MonitoringGroupSection } from "@domain/project/monitoring-product-list/monitoring-group-section";
import { MonitoringProductListAddService } from "@domain/project/monitoring-product-list/monitoring-product-list-add.service";
import {
  AdditionalMonitoringConfigSection,
  MonitoringBomServiceRow,
  MonitoringProductListService,
} from "@domain/project/monitoring-product-list/monitoring-product-list.service";
import { MonitoringProject } from "@domain/project/monitoring-project";
import {
  applyAdditionalProductDrafts,
  createAdditionalProductDrafts,
  createAdditionalProductRollbackState,
  getChangedAdditionalProductSections,
  normalizeAdditionalProductQuantity,
  rollbackAdditionalProductDrafts,
} from "@domain/project/product-list/additional-products-display.util";
import {
  AdditionalCategoryDisplayData,
  buildMonitoringAdditionalCategoryDisplayData,
  buildMonitoringPlacedGroupDisplayData,
  MonitoringSectionDisplayData,
  ProductListGroupDisplayData,
} from "@domain/project/product-list/product-list-display-data.util";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { ModalService } from "@odx/angular/components/modal";
import { MonitoringAdditionalProductsTableComponent } from "@project/project-details/monitoring-product-list/additional-products-table/monitoring-additional-products-table.component";
import { MonitoringProductListSearchComponent } from "@project/project-details/monitoring-product-list/monitoring-product-list-search/monitoring-product-list-search.component";
import { MonitoringProductTableComponent } from "@project/project-details/monitoring-product-list/monitoring-product-table/monitoring-product-table.component";
import { ServiceEditorEntry, ServicesEditorComponent } from "@project/project-details/services-editor/services-editor.component";
import { ToastHelper } from "@utils/toast-helper.service";
import { combineLatest, filter, map, Observable, Subscription } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-monitoring-product-list",
  imports: [
    UiKitModule,
    MonitoringProductTableComponent,
    MonitoringAdditionalProductsTableComponent,
    ServicesEditorComponent,
    MonitoringProductListSearchComponent,
  ],
  templateUrl: "./monitoring-product-list.component.html",
  styleUrl: "./monitoring-product-list.component.scss",
  standalone: true,
})
export class MonitoringProductListComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private monitoringProductListService = inject(MonitoringProductListService);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private modalService = inject(ModalService);
  private toastHelper = inject(ToastHelper);
  private monitoringProductListAddService = inject(MonitoringProductListAddService);

  @Input({ required: true }) set project(value: MonitoringProject) {
    this._project = value;
    this.resetComponentState();
    this.initServicesForm();
    if (this.initialized) {
      this.productListDataInitialized = false;
      this.refreshProductList();
      return;
    }
    this.groupSections = [];
    this.additionalSections = [];
    this.serviceRows = [];
  }

  get project(): MonitoringProject {
    return this._project;
  }

  productListDataInitialized = false;

  servicesForm!: FormGroup;
  groupSections: MonitoringGroupSection[] = [];
  additionalSections: AdditionalMonitoringConfigSection[] = [];
  serviceRows: MonitoringBomServiceRow[] = [];
  editAdditionalProducts = false;
  additionalProductDrafts: Record<string, number> = {};
  protected placedGroupDisplayData: ProductListGroupDisplayData<MonitoringSectionDisplayData, never, MonitoringGroupSection>[] =
    [];
  protected additionalCategoryDisplayData: AdditionalCategoryDisplayData<MonitoringProductData>[] = [];
  protected productsOnFloorPlanExpanded = false;
  protected additionalProductsExpanded = false;
  protected servicesExpanded = false;

  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean>;
  protected readonly editAdditionalProductsLabel = $localize`:@@global.edit:Bearbeiten`;
  protected readonly saveAdditionalProductsLabel = $localize`:@@global.save:Speichern`;
  protected readonly additionalProductsLabel = $localize`:@@productList.additionalProducts:Zusätzliche Produkte`;
  protected readonly canEditAdditionalProducts = toSignal(
    combineLatest([
      this.userService.user$.pipe(map((user) => user.readOnlyAccess)),
      this.projectService.hasUserWritePermission$,
    ]).pipe(map(([isReadOnlyUser, hasWritePermission]) => !isReadOnlyUser && hasWritePermission === true)),
    { initialValue: false },
  );

  private _project!: MonitoringProject;
  private servicesFormSubscription?: Subscription;
  private groupSectionsSubscription?: Subscription;
  private productAddedSubscription?: Subscription;
  private initialized = false;
  private groupExpandedState: Record<string, boolean> = {};
  private productSectionExpandedState: Record<string, boolean> = {};
  private additionalCategoryExpandedState: Record<string, boolean> = {};

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((value): value is boolean => value !== undefined),
    );
  }

  ngOnInit(): void {
    this.initialized = true;
    this.productAddedSubscription = this.monitoringProductListAddService.productAdded$.subscribe(() => {
      this.refreshProductList();
    });
    this.refreshProductList();
  }

  ngOnDestroy(): void {
    this.servicesFormSubscription?.unsubscribe();
    this.groupSectionsSubscription?.unsubscribe();
    this.productAddedSubscription?.unsubscribe();
  }

  protected getCheckboxTestId(id: MonitoringBomServiceId) {
    return `monitoring.productList.checkbox.${id}`;
  }

  protected getNotesControlName(id: MonitoringBomServiceId) {
    return `${id}Notes`;
  }

  protected getNotesTestId(id: MonitoringBomServiceId) {
    return `monitoring.productList.notes.${id}`;
  }

  protected get serviceEditorEntries(): ServiceEditorEntry[] {
    return MONITORING_BOM_SERVICE_DEFINITIONS.map((definition) => ({
      id: definition.id,
      checkboxControlName: definition.id,
      checkboxLabel: definition.label(),
      notesControlName: this.getNotesControlName(definition.id),
      checkboxTestId: this.getCheckboxTestId(definition.id),
      notesTestId: this.getNotesTestId(definition.id),
    }));
  }

  private initGroupSections() {
    this.groupSectionsSubscription?.unsubscribe();
    this.groupSections = [];
    this.serviceRows = [];
    this.groupSectionsSubscription = combineLatest([
      this.monitoringProductListService.createGroupSections(this.project),
      this.monitoringProductListService.createAdditionalConfigSections(this.project),
    ])
      .pipe(
        map(([groupSections, additionalSections]) => ({
          groupSections: groupSections ?? [],
          additionalSections,
        })),
        take(1),
      )
      .subscribe(({ groupSections, additionalSections }) => {
        this.groupSections = groupSections;
        this.additionalSections = additionalSections;
        this.refreshServiceRows();
        this.refreshViewModels();
        this.productListDataInitialized = true;
      });
  }

  private initServicesForm() {
    const formValue = this.buildServicesFormValue();
    if (!this.servicesForm) {
      this.servicesForm = this.formBuilder.group(formValue);
      this.servicesFormSubscription = this.servicesForm.valueChanges.subscribe(() => {
        this.mapServices();
        this.projectService.update(this.project).subscribe();
        this.refreshServiceRows();
      });
      return;
    }

    this.servicesForm.patchValue(formValue, { emitEvent: false });
  }

  private mapServices() {
    this.project.monitoringBomServices.forEach((service) => {
      service.required = this.servicesForm.get(service.id)?.value === true;
      service.notes = this.servicesForm.get(this.getNotesControlName(service.id))?.value ?? "";
    });
    this.project.lastModified = new Date();
  }

  private refreshServiceRows() {
    if (!this.project) {
      this.serviceRows = [];
      return;
    }
    this.serviceRows = this.monitoringProductListService.createServiceRows(
      this.project,
      this.groupSections,
      this.additionalSections,
    );
  }

  private refreshProductList(preserveVisibleContent = false) {
    if (!preserveVisibleContent) {
      this.productListDataInitialized = false;
    }
    this.initGroupSections();
  }

  private buildServicesFormValue(): Record<string, boolean | string> {
    return Object.fromEntries(
      this.project.monitoringBomServices.flatMap((service) => [
        [service.id, service.required],
        [this.getNotesControlName(service.id), service.notes],
      ]),
    );
  }

  protected toggleAdditionalProductsEditMode() {
    if (!this.canEditAdditionalProducts()) {
      return;
    }
    if (this.editAdditionalProducts) {
      this.confirmAdditionalProductsSave();
      return;
    }

    this.additionalProductDrafts = createAdditionalProductDrafts(this.additionalSections);
    this.editAdditionalProducts = true;
    this.refreshAdditionalProductsViewModels();
  }

  protected updateAdditionalProductQuantity(configId: string, delta: number) {
    const currentQuantity = this.additionalProductDrafts[configId] ?? 0;
    this.additionalProductDrafts[configId] = normalizeAdditionalProductQuantity(currentQuantity + delta);
    this.refreshAdditionalProductsViewModels();
  }

  protected setAdditionalProductQuantity(configId: string, quantity: number | string) {
    this.additionalProductDrafts[configId] = normalizeAdditionalProductQuantity(quantity);
    this.refreshAdditionalProductsViewModels();
  }

  protected removeAdditionalProduct(configId: string) {
    this.additionalProductDrafts[configId] = 0;
    this.refreshAdditionalProductsViewModels();
  }

  protected getAdditionalProductsEmptyMessage(categoryLabel: string) {
    return $localize`:@@productList.additionalProducts.emptyAfterEdit.category:Es wurden noch keine zusätzlichen ${categoryLabel}:category: hinzugefügt.`;
  }

  protected getGroupExpanded(groupName: string) {
    return this.groupExpandedState[groupName] ?? false;
  }

  protected setGroupExpanded(groupName: string, expanded: boolean) {
    this.groupExpandedState[groupName] = expanded;
  }

  protected getProductSectionExpanded(groupName: string, sectionName: string) {
    return this.productSectionExpandedState[this.getSectionStateKey(groupName, sectionName)] ?? false;
  }

  protected setProductSectionExpanded(groupName: string, sectionName: string, expanded: boolean) {
    this.productSectionExpandedState[this.getSectionStateKey(groupName, sectionName)] = expanded;
  }

  protected getAdditionalCategoryExpanded(categoryKey: string) {
    return this.additionalCategoryExpandedState[categoryKey] ?? false;
  }

  protected setAdditionalCategoryExpanded(categoryKey: string, expanded: boolean) {
    this.additionalCategoryExpandedState[categoryKey] = expanded;
  }

  private getSectionStateKey(groupName: string, sectionName: string) {
    return `${groupName}:${sectionName}`;
  }

  private confirmAdditionalProductsSave() {
    const changedSections = getChangedAdditionalProductSections(this.additionalSections, this.additionalProductDrafts);
    if (changedSections.length === 0) {
      this.additionalProductDrafts = {};
      this.editAdditionalProducts = false;
      this.refreshAdditionalProductsViewModels();
      return;
    }

    this.modalService
      .open(ConfirmModalComponent, {
        id: "additional-products-confirm-modal",
        data: {
          headerText: $localize`:@@productList.additionalProducts.saveChanges.header:Zusätzliche Produkte aktualisieren`,
          text: $localize`:@@productList.additionalProducts.saveChanges.text:Die Änderungen müssen gespeichert werden, um die zusätzlichen Produkte zu aktualisieren. Beim Verwerfen werden alle Änderungen seit dem Bearbeiten rückgängig gemacht.`,
          discardButtonText: $localize`:@@global.discard:Verwerfen`,
          confirmButtonText: $localize`:@@global.save:Speichern`,
        },
      })
      .onClose$.subscribe((result) => {
        if (result === "discarded") {
          this.additionalProductDrafts = {};
          this.editAdditionalProducts = false;
          this.refreshAdditionalProductsViewModels();
          return;
        }
        if (result !== "confirmed") {
          return;
        }
        const previousQuantities = createAdditionalProductRollbackState(changedSections);
        applyAdditionalProductDrafts(changedSections, this.additionalProductDrafts);
        this.projectService.update(this.project).subscribe({
          next: () => {
            this.additionalProductDrafts = {};
            this.editAdditionalProducts = false;
            this.refreshProductList(true);
            this.toastHelper.success(
              $localize`:@@toast.additionalProducts.saved.header:Speichern erfolgreich`,
              $localize`:@@toast.additionalProducts.saved.message:Die zusätzlichen Produkte wurden erfolgreich gespeichert.`,
            );
          },
          error: () => {
            rollbackAdditionalProductDrafts(changedSections, previousQuantities);
            this.additionalProductDrafts = createAdditionalProductDrafts(this.additionalSections);
            this.refreshAdditionalProductsViewModels();
            this.toastHelper.error(
              $localize`:@@toast.additionalProducts.saveFailed.header:Speichern fehlgeschlagen`,
              $localize`:@@toast.additionalProducts.saveFailed.message:Die zusätzlichen Produkte konnten nicht gespeichert werden.`,
            );
          },
        });
      });
  }

  private refreshViewModels() {
    this.placedGroupDisplayData = buildMonitoringPlacedGroupDisplayData(this.groupSections);
    this.refreshAdditionalProductsViewModels();
  }

  private refreshAdditionalProductsViewModels() {
    this.additionalCategoryDisplayData = buildMonitoringAdditionalCategoryDisplayData(
      this.editAdditionalProducts,
      this.additionalSections,
      this.additionalProductDrafts,
    );
  }

  private resetComponentState() {
    this.editAdditionalProducts = false;
    this.additionalProductDrafts = {};
    this.productsOnFloorPlanExpanded = false;
    this.additionalProductsExpanded = false;
    this.servicesExpanded = false;
    this.groupExpandedState = {};
    this.productSectionExpandedState = {};
    this.additionalCategoryExpandedState = {};
  }
}
